'use client';
import { EventIdentifier, FeatureTeamEvent, TrackingEventType, useAnalytics, useIntl, values } from '@capsicum/common';
import { Alert, Button, Display2 } from '@capsicum/common-web';
import { InfoIcon } from '@capsicum/common-web/icons';
import { type GeetestSuccessResponse, type SmsLoginFormType, useAttemptSchema } from '@capsicum/socios-core';
import { useAtom } from 'jotai';
import { type FC, useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { useTriggerCaptcha } from '../../hooks/useTriggerCaptcha';
import { Shell } from '../../ui';
import { attemptAtom } from '../atoms';

import GeeTestContainer, { type GeeTestRef } from './GeeTestContainer/GeeTestContainer';
import Phone from './Phone';

export type UserDetailsProps = {
	isRequestNewCode?: boolean;
	onCaptchaResponseChange: (newCaptchaResponse: GeetestSuccessResponse) => void;
	validateMobileNumber: (submittedData: SmsLoginFormType) => Promise<boolean>;
};

const UserDetails: FC<UserDetailsProps> = ({ isRequestNewCode, onCaptchaResponseChange, validateMobileNumber }) => {
	const { $t } = useIntl();
	const [attempt, setAttempt] = useAtom(attemptAtom);
	const analytics = useAnalytics();

	// State
	const [geetestReady, setGeetestReady] = useState(false);
	const captchaRef = useRef<GeeTestRef>(null);
	const triggerCaptcha = useTriggerCaptcha({ captcha: captchaRef.current });

	const loginForm = useFormContext<SmsLoginFormType>(); // Holds only the login details without captcha

	const attemptSchema = useAttemptSchema();

	const phoneNumber = useWatch({ control: loginForm.control, name: 'mobileNumber' });

	// Derived State
	const attemptError = useMemo(() => attemptSchema.safeParse(attempt), [attempt, attemptSchema]);

	const allErrors = useMemo(
		() => [
			...values(loginForm.formState.errors).map(v => v.message),
			...(attemptError && !attemptError.success ? [attemptError.error.issues?.[0]?.message] : [])
		],
		[loginForm.formState, attemptError]
	);

	const onSmsLoginFormSubmit = useCallback(
		async (submittedData: SmsLoginFormType) => {
			analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
				feature_team: FeatureTeamEvent.OFT,
				page: 'phone_number_entry_page',
				text: 'Confirm'
			});
			if (captchaRef.current === null) throw new Error('captchaRef is null');

			if (attemptSchema.safeParse(attempt + 1).success && (await validateMobileNumber(submittedData))) {
				const data = await triggerCaptcha({
					metadata: {
						details: {
							countryCode: submittedData.country.iso2,
							mobileNumber: submittedData.mobileNumber
						},
						type: 'phone-number'
					}
				});

				onCaptchaResponseChange({
					captcha_id: data.captchaId,
					captcha_output: data.captchaOutput,
					gen_time: data.genTime,
					lot_number: data.lotNumber,
					pass_token: data.passToken
				});
			}

			setAttempt(x => ++x);
		},
		[setAttempt, attempt, attemptSchema, validateMobileNumber, analytics, triggerCaptcha, onCaptchaResponseChange]
	);

	// JSX

	return (
		<S.Form onSubmit={loginForm.handleSubmit(onSmsLoginFormSubmit)}>
			<Shell
				buttons={[
					<Button
						disabled={
							!geetestReady || !phoneNumber || phoneNumber.length < 1 || loginForm.formState.isSubmitting
						}
						fullWidth
						isLoading={!geetestReady}
						key="confirm"
						testID="login-confirm-button"
						text={$t(isRequestNewCode ? 'auth.requestSubsequentCodeCta' : 'auth.sendFirstCodeCta')}
					/>
				]}
			>
				<S.Container>
					<div>
						<S.Title>{$t('auth.WelcomeTo')}</S.Title>
						<S.BlueTitle>Socios.com</S.BlueTitle>
					</div>

					<S.ContentContainer>
						<S.PhoneNumberLabel data-testid="login-phone-number-label">
							{$t('auth.phoneNumberLabel')}
						</S.PhoneNumberLabel>

						<Phone testID="login-phone" />
					</S.ContentContainer>

					{allErrors?.[0] ? <S.SupportingInputInfo>{allErrors?.[0]}</S.SupportingInputInfo> : null}
				</S.Container>
				<Alert
					closable={false}
					icon={InfoIcon}
					subTitle={$t('auth.instructionalMessage')}
					testID="login-info-alert"
					type="message"
				/>
			</Shell>
			<GeeTestContainer ref={captchaRef} onReady={() => setGeetestReady(true)} />
		</S.Form>
	);
};

namespace S {
	export const Form = styled.form`
		width: 100%;
		height: 100%;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
	`;

	export const Title = styled(Display2)`
		text-transform: uppercase;
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.breakpoints.queries.above.mobile} {
			display: inline;
		}
	`;

	export const BlueTitle = styled(Title)`
		color: ${({ theme }) => theme.colors.text.brand.enabled};

		${({ theme }) => theme.breakpoints.queries.above.mobile} {
			margin-left: 0.4em;
			display: inline;
		}
	`;

	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		height: 100%;
		flex-grow: 1;
	`;

	export const ContentContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
		margin-top: ${({ theme }) => theme.spacings.xxlarge};
	`;

	export const PhoneNumberLabel = styled.div`
		${({ theme }) => theme.fonts.text.text5}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const SupportingInputInfo = styled.div`
		${({ theme }) => theme.fonts.text.caption2};
		color: ${({ theme }) => theme.colors.borders.error.enabled};
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
	`;
}

export default UserDetails;
